import { useEffect, useState } from "react";

const DESKTOP_QUERY = "(min-width: 900px)";

export function useViewport() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return { isDesktop };
}

export interface VisualViewport {
  width: number;
  height: number;
  offsetTop: number;
  offsetLeft: number;
}

export function useVisualViewport(): VisualViewport {
  const [vp, setVp] = useState<VisualViewport>(() => {
    if (typeof window === "undefined")
      return { width: 360, height: 800, offsetTop: 0, offsetLeft: 0 };
    const vv = window.visualViewport;
    return {
      width: vv?.width ?? window.innerWidth,
      height: vv?.height ?? window.innerHeight,
      offsetTop: vv?.offsetTop ?? 0,
      offsetLeft: vv?.offsetLeft ?? 0,
    };
  });

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) {
      const onResize = () =>
        setVp({
          width: window.innerWidth,
          height: window.innerHeight,
          offsetTop: 0,
          offsetLeft: 0,
        });
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
    const update = () =>
      setVp({
        width: vv.width,
        height: vv.height,
        offsetTop: vv.offsetTop,
        offsetLeft: vv.offsetLeft,
      });
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return vp;
}
